import { apiRecords } from "../lib/workerApi";
import React, { useCallback, useEffect, useRef } from "react";
import { pb, API_BASE_URL } from "../lib/pocketbaseConfig";

type AuthProvider = {
  name: string;
  codeVerifier: string;
  authUrl: string;
};

type AuthMethodsResponse = {
  authProviders?: AuthProvider[];
};

export function GoogleOAuthCallbackHandler() {
  const handledRef = useRef(false);

  useEffect(() => {
    const finishGoogleLogin = async () => {
      if (handledRef.current) return;

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (!code) return;

      handledRef.current = true;

      const raw = sessionStorage.getItem("pb_oauth");
      if (!raw) {
        console.error("Missing OAuth session");
        return;
      }

      try {
        const { provider, codeVerifier, redirectUrl, afterLoginPath } =
          JSON.parse(raw);

        const authData = await apiRecords("users")
          .authWithOAuth2Code(provider, code, codeVerifier, redirectUrl);

        const record = authData.record as any;
        const meta = (authData as any).meta || {};
        const rawUser = meta.rawUser || {};

        const fullName = (meta.name || rawUser.name || "").trim();

        const firstName = (
          record.firstName ||
          rawUser.given_name ||
          fullName.split(" ")[0] ||
          ""
        ).trim();

        const lastName = (
          record.lastName ||
          rawUser.family_name ||
          fullName.split(" ").slice(1).join(" ") ||
          ""
        ).trim();

        await apiRecords("users").update(record.id, {
          firstName,
          lastName,
          loginType: "google",
          isGuest: false,
        });

        sessionStorage.removeItem("pb_oauth");

        const finalUrl = `${window.location.origin}${afterLoginPath || "/profile"}`;
        window.location.href = finalUrl;
      } catch (err) {
        console.error("OAuth callback failed:", err);
        sessionStorage.removeItem("pb_oauth");
        window.location.href = `${window.location.origin}/profile`;
      }
    };

    finishGoogleLogin();
  }, []);

  return null;
}

export default function GoogleLoginButton({ label = "Вход с Google" }: { label?: string }) {
  const handleGoogleLogin = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/worker-api/auth/users/auth-methods`,
      );

      if (!res.ok) {
        throw new Error(`Failed to load auth methods: ${res.status}`);
      }

      const methods: AuthMethodsResponse = await res.json();

      const google = methods.authProviders?.find(
        (p: AuthProvider) => p.name === "google",
      );

      if (!google) {
        console.error("Google auth provider not found");
        return;
      }

      const redirectUrl = `${window.location.origin}/`;

      sessionStorage.setItem(
        "pb_oauth",
        JSON.stringify({
          provider: google.name,
          codeVerifier: google.codeVerifier,
          redirectUrl,
          afterLoginPath: "/profile",
        }),
      );

      const authUrl = google.authUrl + encodeURIComponent(redirectUrl);
      window.location.href = authUrl;
    } catch (err) {
      console.error("Google login failed:", err);
    }
  }, []);

  return (
    <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
      <button
        type="button"
        onClick={handleGoogleLogin}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          width: "100%",
          minHeight: 46,
          padding: "10px 16px",
          border: "1px solid #d6e3e2",
          borderRadius: 12,
          cursor: "pointer",
          background: "#fff",
          color: "#243a3a",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: 15,
          fontWeight: 600,
          boxShadow: "0 1px 2px rgba(20, 65, 61, 0.05)",
          transition: "background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.backgroundColor = "#f7fbfa";
          event.currentTarget.style.borderColor = "#9acbc7";
          event.currentTarget.style.boxShadow = "0 3px 10px rgba(20, 65, 61, 0.09)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.backgroundColor = "#fff";
          event.currentTarget.style.borderColor = "#d6e3e2";
          event.currentTarget.style.boxShadow = "0 1px 2px rgba(20, 65, 61, 0.05)";
        }}
      >
        <img
          src="/assets/images/login/google.png" 
          alt="Google"
          style={{ width: 20, height: 20 }}
        />
        {label}
      </button>
    </div>
  );
}
